import { useEffect, useRef, useState } from 'react'
import { Check, X, Zap } from 'lucide-react'
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { chapterApi, type PartialRegenerateLengthMode } from '@/api/chapterApi'
import { correctionApi } from '@/api/correctionApi'
import { friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { NumberInput } from '@/components/ui/number-input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { isDocumentDarkTheme } from '@/lib/documentTheme'
import { cn } from '@/lib/utils'
import { formatCount } from '@/utils/format'
import { runeCount } from '@/utils/partialRegenerate'

interface PartialRegenerateModalProps {
  open: boolean
  projectId?: string | null
  contentLanguage?: string
  chapterId: string | null
  selectedText: string
  start: number
  end: number
  onClose: () => void
  onApplied: () => Promise<void> | void
}

interface FormValues {
  instruction?: string
  length_mode: PartialRegenerateLengthMode
  target_chars?: number
}

interface ChunkPayload {
  delta: string
}

interface DonePayload {
  patch_id: string
  new_text: string
  word_count: number
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isChunkPayload(value: unknown): value is ChunkPayload {
  return isObject(value) && typeof value.delta === 'string'
}

function isDonePayload(value: unknown): value is DonePayload {
  return isObject(value) && typeof value.patch_id === 'string' && typeof value.new_text === 'string' && typeof value.word_count === 'number'
}

function errorMessageFromPayload(value: unknown, fallback: string) {
  if (isObject(value) && typeof value.error === 'string') return friendlyMessage(new Error(value.error), fallback)
  return friendlyMessage(new Error(String(value)), fallback)
}

const lengthModes: PartialRegenerateLengthMode[] = ['similar', 'expand', 'condense', 'custom', 'freeform']

export default function PartialRegenerateModal({
  open,
  projectId,
  contentLanguage,
  chapterId,
  selectedText,
  start,
  end,
  onClose,
  onApplied,
}: PartialRegenerateModalProps) {
  const { t } = useTranslation()
  const closeStreamRef = useRef<(() => void) | null>(null)
  const streamRef = useRef<HTMLDivElement | null>(null)
  const [formValues, setFormValues] = useState<FormValues>({
    instruction: '',
    length_mode: 'similar',
    target_chars: Math.max(1, runeCount(selectedText)),
  })
  const [generating, setGenerating] = useState(false)
  const [applying, setApplying] = useState(false)
  const [generatedText, setGeneratedText] = useState('')
  const [patchId, setPatchId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFormValues({
      instruction: '',
      length_mode: 'similar',
      target_chars: Math.max(1, runeCount(selectedText)),
    })
    setGenerating(false)
    setApplying(false)
    setGeneratedText('')
    setPatchId(null)
    setError(null)
  }, [open, selectedText])

  useEffect(() => {
    return () => {
      closeStreamRef.current?.()
      closeStreamRef.current = null
    }
  }, [])

  useEffect(() => {
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight, behavior: 'smooth' })
  }, [generatedText])

  const finishStream = () => {
    closeStreamRef.current?.()
    closeStreamRef.current = null
    setGenerating(false)
  }

  const handleGenerate = async () => {
    const values = formValues
    if (!chapterId || generating) return
    if (values.length_mode === 'freeform' && !values.instruction?.trim()) {
      toast.warning(t('partialRegenerate.freeformInstructionRequired'))
      return
    }
    if (values.length_mode === 'custom' && (!values.target_chars || values.target_chars < 1)) {
      toast.error(t('partialRegenerate.targetCharsRequired'))
      return
    }

    closeStreamRef.current?.()
    setGenerating(true)
    setGeneratedText('')
    setPatchId(null)
    setError(null)

    closeStreamRef.current = chapterApi.partialRegenerateStream(
      chapterId,
      {
        start,
        end,
        selected_text: selectedText,
        instruction: values.instruction?.trim() || undefined,
        length_mode: values.length_mode,
        target_chars: values.length_mode === 'custom' ? values.target_chars : undefined,
        lang: contentLanguage || undefined,
      },
      {
        onEvent: (event, data) => {
          if (event === 'chunk') {
            if (isChunkPayload(data)) setGeneratedText((prev) => prev + data.delta)
            return
          }
          if (event === 'done') {
            if (!isDonePayload(data)) {
              const nextError = t('partialRegenerate.invalidDonePayload')
              setError(nextError)
              toast.error(nextError)
              finishStream()
              return
            }
            setGeneratedText(data.new_text)
            setPatchId(data.patch_id)
            finishStream()
            return
          }
          if (event === 'error') {
            const nextError = errorMessageFromPayload(data, t('common.serviceError'))
            setError(nextError)
            toast.error(nextError)
            finishStream()
          }
        },
        onError: (err) => {
          const nextError = friendlyMessage(err, t('common.serviceError'))
          setError(nextError)
          toast.error(nextError)
          finishStream()
        },
      },
    )
  }

  const handleClose = () => {
    closeStreamRef.current?.()
    closeStreamRef.current = null
    setGenerating(false)
    onClose()
  }

  const handleDiscard = async () => {
    if (!chapterId || !patchId) {
      handleClose()
      return
    }
    setApplying(true)
    try {
      await chapterApi.discardPatch(chapterId, patchId)
      toast.success(t('partialRegenerate.discarded'))
      handleClose()
    } catch (err) {
      toast.error(friendlyMessage(err, t('common.serviceError')))
    } finally {
      setApplying(false)
    }
  }

  const handleApply = async () => {
    if (!chapterId || !patchId) return
    setApplying(true)
    try {
      await chapterApi.confirmPatch(chapterId, patchId)
      if (projectId && selectedText.trim() && generatedText.trim()) {
        try {
          await correctionApi.create(projectId, {
            scope: 'style',
            rule_text: buildCorrectionDraftText(selectedText, generatedText),
            reason: '由章节差异标注生成，待确认后进入纠偏库。',
            patch_id: patchId,
            strength: 'soft',
            source: {
              patch_id: patchId,
              original_snippet: selectedText,
              user_correction: generatedText,
            },
          })
          toast.success('纠偏草稿已创建')
        } catch (err) {
          toast.warning(friendlyMessage(err, '章节已应用，纠偏草稿创建失败'))
        }
      }
      await onApplied()
      toast.success(t('partialRegenerate.applied'))
      handleClose()
    } catch (err) {
      toast.error(friendlyMessage(err, t('common.serviceError')))
    } finally {
      setApplying(false)
    }
  }

  const lengthMode = formValues.length_mode
  const hasPatch = Boolean(patchId)
  const controlsDisabled = generating || applying
  const canDismiss = !generating && !applying
  const useDarkTheme = isDocumentDarkTheme()

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && canDismiss) handleClose()
      }}
    >
      <DialogContent
        className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-[920px]"
        showCloseButton={canDismiss}
        onEscapeKeyDown={(event) => {
          if (!canDismiss) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (!canDismiss) event.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('partialRegenerate.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error ? (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <div>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm font-medium">{t('partialRegenerate.original')}</span>
              <span className="text-sm text-muted-foreground">
                {t('partialRegenerate.selectedCount', { value: formatCount(runeCount(selectedText)) })}
              </span>
            </div>
            <div className="mt-2 max-h-[150px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm">
              {selectedText}
            </div>
          </div>

          <div className={cn('space-y-4', controlsDisabled && 'pointer-events-none opacity-60')}>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="partial-regenerate-instruction">
                {t('partialRegenerate.instruction')}
              </label>
              <textarea
                id="partial-regenerate-instruction"
                className="min-h-[84px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                maxLength={1200}
                placeholder={t('partialRegenerate.instructionPlaceholder')}
                value={formValues.instruction ?? ''}
                disabled={controlsDisabled}
                onChange={(event) => setFormValues((prev) => ({ ...prev, instruction: event.target.value }))}
              />
              <div className="text-right text-xs text-muted-foreground">
                {(formValues.instruction ?? '').length}/1200
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">{t('partialRegenerate.lengthMode')}</div>
              <ToggleGroup
                type="single"
                value={formValues.length_mode}
                disabled={controlsDisabled}
                onValueChange={(value) => {
                  if (!value) return
                  setFormValues((prev) => ({ ...prev, length_mode: value as PartialRegenerateLengthMode }))
                }}
              >
                {lengthModes.map((mode) => (
                  <ToggleGroupItem key={mode} value={mode}>
                    {t(`partialRegenerate.modes.${mode}`)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            {lengthMode === 'custom' ? (
              <div className="max-w-[180px] space-y-2">
                <label className="text-sm font-medium" htmlFor="partial-regenerate-target-chars">
                  {t('partialRegenerate.targetChars')}
                </label>
                <NumberInput
                  id="partial-regenerate-target-chars"
                  min={1}
                  value={formValues.target_chars}
                  disabled={controlsDisabled}
                  onChange={(value) => setFormValues((prev) => ({ ...prev, target_chars: value }))}
                />
              </div>
            ) : null}
          </div>

          {generating || generatedText ? (
            <div ref={streamRef} className="max-h-[420px] overflow-auto">
              <div className="text-sm font-medium">{hasPatch ? t('partialRegenerate.diffPreview') : t('partialRegenerate.streamingPreview')}</div>
              {hasPatch ? (
                <div className="mt-2">
                  <ReactDiffViewer
                    oldValue={selectedText}
                    newValue={generatedText}
                    splitView
                    compareMethod={DiffMethod.WORDS}
                    leftTitle={t('partialRegenerate.original')}
                    rightTitle={t('partialRegenerate.rewritten')}
                    useDarkTheme={useDarkTheme}
                    hideSummary
                    disableWorker
                  />
                </div>
              ) : (
                <div className="mt-2 min-h-[120px] whitespace-pre-wrap rounded-md border bg-background p-3 text-sm">
                  {generatedText || t('partialRegenerate.waiting')}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          {hasPatch ? (
            <Button variant="outline" onClick={() => void handleDiscard()} disabled={applying}>
              <X />
              {t('partialRegenerate.discard')}
            </Button>
          ) : (
            <Button variant="outline" onClick={handleClose} disabled={generating || applying}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            onClick={() => void handleGenerate()}
            disabled={generating || applying}
          >
            <Zap />
            {hasPatch ? t('partialRegenerate.regenerate') : t('partialRegenerate.generate')}
          </Button>
          <Button onClick={() => void handleApply()} disabled={!hasPatch || generating || applying}>
            <Check />
            {t('partialRegenerate.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function buildCorrectionDraftText(original: string, rewritten: string): string {
  const oldText = compactSnippet(original)
  const newText = compactSnippet(rewritten)
  return `避免继续使用原表达「${oldText}」，优先沿用用户修订方向「${newText}」。`
}

function compactSnippet(value: string): string {
  const text = value.replace(/\s+/g, ' ').trim()
  return text.length > 80 ? `${text.slice(0, 80)}...` : text
}
